<template>
  <div class="plan-route-container">
    <plan-route-header
      :client-data="clientData"
      @back-to-planner="backToPlanner"
    />
    <div class="wrapper">
      <alert-message
        :alert-message="alertObject.message"
        :alert-value="alertObject.value"
        :alert-type="alertObject.type"
        :top-position="44"
        @disable-alert="alertObject.value = false"
      />
      <div class="calendar_wrap">
        <div class="calendar text-xs-center">
          CALENDAR
          <calendar-new-component
            :calendar-object="calendarObject"
            @set-modal-values="setModalValues($event)"
            @on-calendar-clicked="displayRouteDetails($event)"
          />
        </div>
      </div>
      <div
        class="middle-blocks"
        :class="{ single: secondaryLayout, 'disable-element': !clientChanged }"
      >
        <template v-if="!secondaryLayout">
          <div
            class="choose_time"
            :class="{
              'block-filled': chosenTime && chosenDate,
              'disable-element': isPastRoute(selectedRoute)
            }"
          >
            <div class="block-heading">
              CHOOSE DATE & TIME
            </div>
            <div class="two-entries">
              <div class="date">
                <date-picker
                  :picker-date="dateFromRoute"
                  @changeDate="dateUpdater"
                />
              </div>
              <div class="time">
                <time-picker-alt
                  class="time"
                  :min-time="getMinTime()"
                  :picker-time="timeFromRoute"
                  :time-error="timeError"
                  :clear-timer="clearTimer"
                  @reset-timer="clearTimer = false"
                  @changeTime="timeUpdater"
                  @disable-calculate="chosenTime = ''"
                />
              </div>
            </div>
          </div>
          <start-end-location
            :client-changed="clientChanged"
            :start-location-list="startLocationList"
            :first-block-completed="isFirstBlockCompleted"
            :route="currentRoute"
            :set-start-end="setStartEnd"
            :reset-start-end="resetStartEnd"
            :reset-end-location="resetEndLocation"
            @set-start-end="setStartEnd = false"
            @reset-start-end="resetStartEnd = false"
            @reset-end-location="resetEndLocation = false"
            @start-end-state="isCalc = $event"
            @activate-route-edit-mode="activateRouteEditMode"
            @on-change="onStartEndChange"
          />
          <viewing-time-selector
            :initial-value="initialViewTime"
            :is-calc="isCalc"
            @on-change="onViewingTimeChange"
          />
        </template>
        <div
          class="calculate"
          :class="{
            'block-filled': isRouteCalculated,
            'small-padding': secondaryLayout
          }"
        >
          <div v-if="selectedRouteLoading" class="mt-5">
            <v-progress-circular indeterminate color="grey darken-1" />
            <div class="small py-3">
              {{ getLoadingText() }}
            </div>
          </div>
          <template v-else>
            <route-basic-info
              v-if="displayRouteTopInfo"
              :route-object="selectedRoute"
              :route-start="selectedRoute.calculatedArray[0].Address"
            />

            <div v-else-if="isSavedRoute" class="block-heading">
              <span
                class="route-name text-medium"
              >{{ selectedRoute.name.toUpperCase() }} -&nbsp;</span>
              <span :class="[selectedRoute.state, 'route-name']">{{
                selectedRoute.state.toUpperCase()
              }}</span>
            </div>
            <div v-else class="block-heading">
              CALCULATE ROUTE
            </div>
            <updated-list-component
              :table-headers="routeHeaders"
              :table-data="updatedTableItems"
              style="margin-top: 20px"
              @clicked="confirmationToRemove"
            />
            <plan-route-buttons
              :is-calculating="isCalculating"
              :disable-calculate="disableCalculate || !viewTime"
              :hide-calculate="hideCalculate"
              :null-times="isRouteCalculated"
              @calculate="calculate"
              @cancel-route="cancelRoute"
              @new-route-page="newRoutePage"
              @request-showing="requestShowing"
              @send-schedule="sendSchedule"
              @save-clicked="saveBtnClicked"
            />
          </template>
        </div>
      </div>
      <div class="rightside" :class="{ 'disable-element': !clientChanged }">
        <add-home-to-route
          :client-changed="clientChanged"
          :secondary-layout="secondaryLayout"
          :requested-tour-to-add="requestedTourToAdd"
          :route="currentRoute"
          @search-properties="searchProperties($event)"
          @add-requested-homes="addRequestedHomes"
        />
        <div class="map">
          <google-location-map
            v-if="displayMap"
            :key="selectedRoute ? selectedRoute._id : 1"
            :route="currentRoute"
          />
        </div>
      </div>
      <v-dialog v-model="openNameModal" width="550px">
        <route-name-modal
          :initial-name="routeName"
          @route-name-entered="nameReceived"
        />
      </v-dialog>
    </div>
    <v-dialog
      v-model="confirmationModal.status"
      :width="alert.width"
      :max-width="alert.maxWidth"
    >
      <confirmation-modal
        :modal-options="confirmationModal"
        @on-negative-clicked="confirmModalNegative"
        @on-positive-clicked="confirmModalPositive"
      />
    </v-dialog>
    <full-screen-spinner v-if="waitForProgress" :spinner-text="spinnerText" />
    <content-with-button-modal
      :modal-params="warningModalParams"
      :show-modal="displayWarningModal"
      @close-modal="closeWarningModal($event)"
    />
  </div>
</template>

<script>
import moment from 'moment'
import { mapState, mapMutations, mapActions } from 'vuex'
import PlanRouteHeader from './PlanRouteHeader.vue'
import CalendarNewComponent from '../../calendar/CalendarNewComponent'
import DatePicker from './components/DatePicker.vue'
import TimePickerAlt from './components/TimePickerAlt.vue'
import UpdatedListComponent from '../../uiUtils/UpdatedListComponent.vue'
import {
  propertyFullAddress,
  roundOffMinutes
} from '../../../utils/propertyUtils'

import PlanRouteButtons from '../../agent/plan-route/components/PlanRouteButtons.vue'
import AlertMessage from '../../uiUtils/AlertMessage.vue'
import RouteNameModal from '../../modals/RouteNameModal.vue'
import ConfirmationModal from '../../modals/ConfirmationModal.vue'
import ContentWithButtonModal from '../../modals/ContentWithButtonModal.vue'
import FullScreenSpinner from '../../uiUtils/FullScreenSpinner.vue'
import GoogleLocationMap from '../../googlemap/GoogleLocationMap.vue'
import RouteBasicInfo from './components/RouteBasicInfo.vue'
import StartEndLocation from './components/StartEndLocation.vue'
import ViewingTimeSelector from './components/ViewingTimeSelector.vue'
import AddHomeToRoute from './components/AddHomeToRoute.vue'

import {
  INITIAL_SEARCH_QUERY,
  SEARCH_LIMIT,
  INITIAL_NEW_QUERY,
  HIDE_STATUSES
} from '../../common/searchPropertyConst.js'
import { genericPostRequestToApi } from '../../../utils/genericRequests'
import { RetrieveSearchPropertyUrl } from '../../../utils/agentUrls'
import setRoute from '../../../services/directionService'
import {
  isEmpty,
  toCapitalCase,
  sortObjectArrayByKey
} from '../../../utils/generalUtils'
import {
  filterRouteByDate,
  isTimePresent,
  isCancelledRoute
} from '../../../utils/routeUtils'
import { DEFAULT_CONFIRMATION_MODAL_VALUE } from '../../../utils/generalConsts'
import {
  hasCalculatedArrayTime,
  hasDirectionRoute,
  resetCalculatedTimes,
  hasCustomStartStop,
  hasCustomEndStop,
  isCustomStopCalculated,
  routeDirectionCalculateCheck
} from '../../../utils/planRouteUtils'

import routeDataContent from '../../mixins/routeDataContent'
import refreshClientData from '../../mixins/refreshClientData'
import {
  requestRouteShowing,
  sendScheduleRequest,
  updateSavedRoutesOnPendingRequest,
  saveRouteRequest,
  updateRouteRequest,
  cancelDeleteRoute
} from '../../../services/routeService'
import { sendRouteSchedule } from '../../../services/messagesService'

export default {
  components: {
    PlanRouteHeader,
    CalendarNewComponent,
    DatePicker,
    TimePickerAlt,
    UpdatedListComponent,
    PlanRouteButtons,
    AlertMessage,
    RouteNameModal,
    ConfirmationModal,
    ContentWithButtonModal,
    FullScreenSpinner,
    GoogleLocationMap,
    RouteBasicInfo,
    StartEndLocation,
    ViewingTimeSelector,
    AddHomeToRoute
  },

  mixins: [routeDataContent, refreshClientData],

  data: () => ({
    startLocationList: [],
    newCustomRoute: {},
    updatedTableItems: [],
    displayMap: false,
    isCalc: false,
    resetStartEnd: false,
    setStartEnd: false,
    resetEndLocation: false,
    routeTimeSpan: [],
    clearTimer: false,
    searchQuery: { ...INITIAL_SEARCH_QUERY, maxArea: '', maxPrice: '' },
    routeHeaders: [
      {
        text: '',
        value: 'remove',
        sortable: false,
        width: '10%'
      },
      {
        text: 'Home Address',
        value: 'address',
        width: '70%',
        sortable: false
      },
      {
        text: 'ETA',
        value: 'arrivalTime',
        sortable: false,
        width: '30%',
        align: 'center'
      }
    ],
    timeError: false,
    calendarObject: {
      start: moment().format('YYYY-MM-DD'),
      type: 'day',
      now: null,
      display: 'agent',
      routeModal: false,
      class: 'plan-route',
      font: '1.1rem'
    },
    alertObject: {
      type: 'success',
      value: false,
      message: ''
    },
    routeName: '',
    chosenDate: moment().format('MM/DD/YYYY'),
    chosenTime: '',
    dateFromRoute: '',
    timeFromRoute: '',
    selectedHousesMutable: [],
    requestedTourToAdd: [],
    addressNumberFromChild: '',
    openNameModal: false,
    selectedForRemoval: null,
    confirmationModal: Object.assign({}, DEFAULT_CONFIRMATION_MODAL_VALUE),
    waitForProgress: false,
    spinnerText: null,
    isCalculating: false,
    warningModalParams: {},
    displayWarningModal: false,
    foundProperty: null,
    viewTime: 20,
    initialViewTime: null
  }),

  computed: {
    ...mapState('client', ['clientFavorites', 'clientHomes', 'clientChanged']),
    ...mapState('common', [
      'selectedRoute',
      'selectedRouteLoading',
      'routeHomes'
    ]),
    ...mapState('agent', ['rhtValue', 'favorableViewTime']),
    ...mapState('style', ['alert']),

    displayRouteTopInfo() {
      if (this.isSelectedRoute && this.secondaryLayout) {
        if (!isEmpty(this.selectedRoute.calculatedArray)) return true
      }
      return false
    },

    filteredRhtValue() {
      return this.rhtValue.data.filter(
        item => !HIDE_STATUSES.includes(item.Status)
      )
    },

    isRouteCalculated() {
      let isCalculated = false
      this.updatedTableItems.forEach(item => {
        if (!isEmpty(item[2].text)) isCalculated = true
      })
      return isCalculated
    },

    currentRoute() {
      return !isEmpty(this.newCustomRoute)
        ? JSON.parse(JSON.stringify(this.newCustomRoute))
        : !isEmpty(this.selectedRoute)
          ? JSON.parse(JSON.stringify(this.selectedRoute))
          : {}
    },

    isCustomStart() {
      return hasCustomStartStop(this.currentRoute.calculatedArray)
    },

    isCustomEnd() {
      return hasCustomEndStop(this.currentRoute.calculatedArray)
    },

    isSelectedRoute() {
      return !isEmpty(this.selectedRoute)
    },

    isSavedRoute() {
      return this.isSelectedRoute && this.selectedRoute.state === 'saved'
    },

    disableCalculate() {
      if (!this.isCalc) return true
      if (isEmpty(this.currentRoute.calculatedArray)) return true

      const calculatedArray = [...this.currentRoute.calculatedArray]
      if (hasCustomStartStop(calculatedArray)) {
        return this.emptyStartLocation || !this.isFirstBlockCompleted
      }
      if (hasCustomEndStop(calculatedArray)) {
        return this.emptyEndLocation || !this.isFirstBlockCompleted
      }

      return !this.isFirstBlockCompleted || !this.viewTime
    },

    hideCalculate() {
      if (this.isSelectedRoute) {
        if (this.selectedRoute.state === 'saved' && !this.routeEditMode) {
          return true
        }
        if (this.selectedRoute.state !== 'saved') return true
      }
      return false
    },

    isFirstBlockCompleted() {
      return !isEmpty(this.chosenDate) && !isEmpty(this.chosenTime)
    },

    emptyStartLocation() {
      return isEmpty(this.selectedStartLocation)
    },

    emptyEndLocation() {
      return (
        isEmpty(this.selectedEndLocation) ||
        (this.selectedEndLocation && this.selectedEndLocation.text === '')
      )
    },

    secondaryLayout() {
      if (this.isSelectedRoute) {
        return (
          this.isPastRoute(this.selectedRoute) ||
          this.selectedRoute.state !== 'saved'
        )
      }
      return false
    }
  },

  watch: {
    rhtValue: {
      handler: function(value) {
        if (!isEmpty(value)) {
          this.setSelectedHomesDropdown(this.currentRoute)
        }
      },
      deep: true
    },

    agentRouteData: {
      handler: function(value) {
        this.setRouteTimeSpan(value, this.chosenDate)
      },
      deep: true
    },

    chosenDate(value) {
      this.setRouteTimeSpan(this.agentRouteData, value)
    },

    selectedRoute: {
      handler: async function(newValue) {
        if (!isEmpty(newValue)) {
          const updatedRoute = routeDirectionCalculateCheck(this.selectedRoute)
            ? await this.calculateRouteHomeMarkerArray(this.selectedRoute, true)
            : { ...this.selectedRoute }
          this.initRouteDetails(updatedRoute)
        }
      },
      deep: true
    },

    newCustomRoute: {
      handler: function(newValue) {
        if (!isEmpty(newValue)) {
          this.customRouteParamSetting()
        }
      },
      deep: true
    },

    clientData: {
      handler: function(newValue, oldValue) {
        const clientRoutes = this.agentRouteData.filter(
          route => route.clientId === newValue._id
        )

        if (!isEmpty(clientRoutes)) {
          this.setFavorableViewTime(clientRoutes[0].viewingTimeMins || 20)
          this.initialViewTime = clientRoutes[0].viewingTimeMins || 20
        } else {
          this.setFavorableViewTime(20)
          this.initialViewTime = 20
        }

        if (isEmpty(this.selectedRoute) && isEmpty(this.routeHomes)) {
          if (
            newValue.homes &&
            newValue.homes.length > 0 &&
            !isEmpty(oldValue)
          ) {
            this.waitForProgress = true
            this.spinnerText = 'Loading client data...'
            setTimeout(() => {
              this.waitForProgress = false
            }, 5000)
          }
          this.newRoutePage()
        }
      },
      deep: true
    },

    clientFavorites(newValue) {
      if (!isEmpty(newValue) && this.clientChanged) {
        this.initPlanRouteData(newValue)
      }
    }
  },

  created() {
    window.scrollTo(0, 0)
    if (this.clientFavorites && this.clientChanged) {
      this.initPlanRouteData(this.clientFavorites)
    }
  },

  async mounted() {
    if (!isEmpty(this.selectedRoute)) {
      const updatedRoute = routeDirectionCalculateCheck(this.selectedRoute)
        ? await this.calculateRouteHomeMarkerArray(this.selectedRoute, true)
        : { ...this.selectedRoute }
      this.setStartEnd = true
      this.initRouteDetails(updatedRoute)
    } else {
      this.initialViewTime = this.favorableViewTime
    }
    this.setRouteTimeSpan(this.agentRouteData, this.chosenDate)
  },

  methods: {
    ...mapActions('agent', ['loadRequestedHomeData']),
    ...mapMutations('common', {
      setSelectedRoute: 'SET_SELECTED_ROUTE',
      setSelectedRouteLoading: 'SET_SELECTED_ROUTE_LOADING',
      setRouteHomes: 'SET_ROUTE_HOMES'
    }),
    ...mapMutations('agent', {
      setRht: 'SET_RHT',
      setFavorableViewTime: 'SET_FAVORABLE_VIEW_TIME'
    }),

    setRouteTimeSpan(value, date) {
      const filteredValue = value.filter(item => !isCancelledRoute(item))
      if (!isEmpty(filteredValue) && date) {
        this.routeTimeSpan = filterRouteByDate(
          this.selectedRoute,
          filteredValue,
          date
        )
      }
    },

    initRouteDetails(routeData) {
      if (!isEmpty(this.routeHomes)) this.setRouteHomes([])
      this.initialViewTime = routeData.viewingTimeMins
      this.viewTime = routeData.viewingTimeMins
      this.selectedRouteUpdated(routeData)
      this.setSelectedHomesDropdown(routeData)
      this.newCustomRoute = JSON.parse(JSON.stringify(routeData))
    },

    selectedRouteUpdated(route) {
      if (route.returnedRoute || route.markers) {
        this.selectedRouteUpdatedChange(route.calculatedArray)
      }
      this.dateFromRoute = moment(route.startTime).format('YYYY-MM-DD')
      this.timeFromRoute = moment(route.startTime).format('hh:mm A')

      this.chosenDate = moment(this.dateFromRoute, 'YYYY-MM-DD').format(
        'MM/DD/YYYY'
      )
      this.chosenTime = this.timeFromRoute
      this.routeName = route.name
      Object.assign(this.calendarObject, {
        start: moment(this.chosenDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
      })
    },

    selectedRouteUpdatedChange(calculatedArray) {
      this.calculateTableItems(calculatedArray)
      this.displayMap = true
      this.setRouteCalculatedArray(calculatedArray)
      this.setSelectedRouteLoading(false)
    },

    customRouteParamSetting() {
      if (!isEmpty(this.currentRoute.fullCalculatedArray)) {
        const calculatedArray = [...this.currentRoute.calculatedArray]
        this.calculateTableItems(calculatedArray)
        this.displayMap = true
        this.setSelectedHomesDropdown(this.currentRoute)
        if (calculatedArray.length > 0) {
          this.setStartEndLocationList(this.currentRoute.calculatedArray)
        }
      }
    },

    getLoadingText() {
      return this.clientChanged
        ? 'Populating Routes...'
        : 'Loading Client Data and route...'
    },

    initPlanRouteData(data) {
      this.waitForProgress = false
      this.spinnerText = null

      if (!isEmpty(data) && !isEmpty(this.routeHomes)) {
        const calculatedArray = this.routeHomes.map(home => ({
          Address: propertyFullAddress(home),
          arrivalTime: null,
          latitude: home.Latitude,
          longitude: home.Longitude,
          propertyId: home._id
        }))

        this.newCustomRoute = {
          ...this.newCustomRoute,
          fullCalculatedArray: [...this.routeHomes],
          calculatedArray,
          markers: this.setMarkers(calculatedArray),
          returnedRoute: null
        }
        this.customRouteParamSetting()
      }
    },

    backToPlanner() {
      this.setSelectedRoute({})
      this.newCustomRoute = {}
      this.clearCustomStartEnd()
      this.setSelectedRouteLoading(false)
      this.setRouteHomes([])
      this.$router.push({ name: 'planner' })
    },

    setSelectedHomesDropdown(route) {
      this.requestedTourToAdd = []
      let newTourHomes = []
      if (!isEmpty(route)) {
        const houseIds = route
          ? route.fullCalculatedArray.map(house => house._id)
          : []
        newTourHomes = this.filteredRhtValue.filter(
          home => !houseIds.includes(home.propertyId)
        )
      } else {
        newTourHomes = JSON.parse(JSON.stringify(this.filteredRhtValue))
      }
      for (const favorite of newTourHomes) {
        this.requestedTourToAdd.push({ ...favorite, checkbox: false })
      }
    },

    // Function to set start and end location in the middle box
    // based on the current active route or custom route
    // This function also considers whether it's custom stop or not
    setStartEndLocationList(fromArray) {
      const firstElement = fromArray[0]
      if (hasCustomStartStop(fromArray)) {
        this.setSelectedStartLocation({ ...firstElement })
      }

      if (hasCustomEndStop(fromArray)) {
        const lastHome = fromArray[fromArray.length - 1]
        this.setSelectedEndLocation({ ...lastHome })
      }

      this.startLocationList = fromArray
        .map(item => item)
        .filter(start => start.propertyId !== null)
    },

    // Function to handle change in dropdown from start/end block
    onStartEndChange({ data, type }) {
      let updatedCalculatedArray = this.fieldValuesChanged()

      let selectedProperty = null
      updatedCalculatedArray = updatedCalculatedArray.filter(item => {
        if (item.propertyId !== data.propertyId) return true
        else selectedProperty = item
      })

      if (selectedProperty) {
        type === 'start'
          ? updatedCalculatedArray.unshift(selectedProperty)
          : updatedCalculatedArray.push(selectedProperty)
      }

      let updatedHomeArray = [...this.currentRoute.fullCalculatedArray]
      if (data.propertyId) {
        let selectedHome = null
        updatedHomeArray = updatedHomeArray.filter(item => {
          if (item._id !== data.propertyId) return true
          else selectedHome = item
        })
        if (selectedHome) {
          type === 'start'
            ? updatedHomeArray.unshift(selectedHome)
            : updatedHomeArray.push(selectedHome)
        }
      }

      this.newCustomRoute = {
        ...this.currentRoute,
        fullCalculatedArray: [...updatedHomeArray],
        calculatedArray: [...updatedCalculatedArray],
        markers: this.setMarkers(updatedCalculatedArray),
        returnedRoute: null
      }
    },

    fieldValuesChanged() {
      if (!this.routeEditMode) {
        this.setRouteEditMode(true)
      }

      let updatedCalculatedArray = [...this.currentRoute.calculatedArray]

      if (hasCalculatedArrayTime(updatedCalculatedArray)) {
        updatedCalculatedArray = this.resetStopTimes(updatedCalculatedArray)
      }
      return updatedCalculatedArray
    },

    // Function to handle change in view time from the view time block
    onViewingTimeChange(value) {
      this.viewTime = value
      this.initialViewTime = null
      if (!this.routeEditMode) {
        this.setRouteEditMode(true)
      }
      // const updatedCalculatedArray = this.fieldValuesChanged()
      // this.newCustomRoute = {
      //   ...this.currentRoute,
      //   calculatedArray: [...updatedCalculatedArray]
      // }
    },

    async searchProperties(mlsNumber) {
      let requestedQuery = JSON.parse(JSON.stringify(INITIAL_NEW_QUERY))
      requestedQuery = { ...requestedQuery, MLSNumber: mlsNumber }

      const searchElement = {
        index: {
          skip: 0,
          limit: SEARCH_LIMIT
        },
        query: requestedQuery
      }

      const response = await genericPostRequestToApi(
        RetrieveSearchPropertyUrl(),
        searchElement
      )

      // Use modals to indicate different results
      if (isEmpty(response.data)) {
        this.openWarningModal(
          `Property with MLS Number ${mlsNumber} doesn't exist in MLS database or doesn't have an Active status.`
        )
      } else if (response.data.length > 0) {
        const propertyId = response.data[0]._id
        const isFound = this.clientData.homes.find(
          fav => fav.propertyId === propertyId
        )
        if (!isEmpty(isFound)) {
          if (isFound.requestTour) {
            this.openWarningModal(
              `Property with MLS Number ${mlsNumber} already exists in Requested Homes list.`
            )
          } else {
            Object.assign(this.confirmationModal, {
              status: true,
              message: `Property with MLS Number ${mlsNumber} found in client's home list. Do you want to add that to requested homes?`,
              positive: 'Yes, add it',
              negative: 'No, not now'
            })
            this.foundProperty = isFound
          }
        } else {
          Object.assign(this.confirmationModal, {
            status: true,
            message: `Property with MLS Number ${mlsNumber} found in MLS database. Do you want to add it to client homes and requested homes?`,
            positive: 'Yes, add it',
            negative: 'No, not now'
          })
          this.foundProperty = response.data[0]
        }
      }
    },

    addRequestedHomes() {
      this.setRouteEditMode(true)
      this.resetEndLocation = true
      // Gets houses that were selected to add into route
      const selectedToAdd = this.requestedTourToAdd.filter(
        tour => tour.checkbox === true
      )

      const updatedHomes =
        !isEmpty(this.newCustomRoute) &&
        !isEmpty(this.newCustomRoute.fullCalculatedArray)
          ? [...this.newCustomRoute.fullCalculatedArray, ...selectedToAdd]
          : [...selectedToAdd]
      this.updateRouteObjectPropertyParams(updatedHomes)

      // Puts all checkbox back to false
      for (const requested of this.requestedTourToAdd) {
        requested.checkbox = false
      }
    },

    openWarningModal(message) {
      Object.assign(this.warningModalParams, { title: message, btnText: 'Ok' })
      this.displayWarningModal = true
    },

    calculateTableItems(fromArray) {
      let response = []
      let alphabet = 'A'
      fromArray.map((item, index) => {
        if (item) {
          let obj = {}
          let propertyETA = item.arrivalTime

          if (
            (this.isSelectedRoute && !this.isSavedRoute) ||
            this.isPastRoute(this.currentRoute)
          ) {
            obj[0] = { text: '' }
          } else {
            obj[0] = {
              type: 'clickableIcon',
              text: 'close',
              btnClass: 'white--text grey',
              cellClass: 'pr-0',
              data: item
            }
          }

          if (!item.propertyId) {
            obj[1] = {
              type: 'text',
              text: item.Address,
              preText: String.fromCharCode(alphabet.charCodeAt(0) + index),
              preTextClass: 'hwayzBlue font-weight-medium title',
              class: 'text-xs-left'
            }
          } else {
            obj[1] = {
              type: 'address',
              text: item.Address || this.getFullAddress(item),
              preText: String.fromCharCode(alphabet.charCodeAt(0) + index),
              preTextClass: 'hwayzBlue font-weight-medium title',
              data: item
            }
          }

          obj[2] = {
            text: isEmpty(propertyETA)
              ? ''
              : moment(propertyETA).format('h:mm A')
          }
          response.push(obj)
        }
      })
      this.updatedTableItems = response
    },

    async displayRouteDetails(routeData) {
      this.setSelectedRouteLoading(true)
      this.displayMap = false
      this.setStartEnd = true

      const newClient = this.agentData.clients.find(
        client => client._id === routeData.clientId
      )
      if (newClient._id !== this.clientData._id) {
        this.loadRequestedHomeData({ clientData: newClient, loadFavs: true })
      }

      if (this.routeEditMode || routeDirectionCalculateCheck(routeData)) {
        await this.calculateRouteHomeMarkerArray(routeData, true)
      } else {
        this.setSelectedRoute(routeData)
        this.clearCustomStartEnd()
        this.displayMap = true
      }
      this.setRouteEditMode(false)
      this.setStartEnd = false
      this.setSelectedRouteLoading(false)
    },

    clearCustomStartEnd() {
      this.setSelectedStartLocation({})
      this.setSelectedEndLocation({})
    },

    confirmationToRemove(houseData) {
      this.selectedForRemoval = houseData.propertyId
        ? this.newCustomRoute.fullCalculatedArray.find(
          item => item.propertyId === houseData.propertyId
        )
        : houseData
      Object.assign(this.confirmationModal, {
        status: true,
        message: `Do you really want to remove this property from adding to the route`,
        positive: 'Yes, remove it',
        negative: 'No, keep it'
      })
    },

    async confirmModalPositive() {
      if (this.foundProperty) {
        // Handling positive click for new MLS adding modal
        let requestedProperty = null
        if (this.foundProperty.propertyId) {
          requestedProperty = await this.requestTourForProperty(
            this.foundProperty
          )
          this.foundProperty = {
            ...this.foundProperty,
            ...this.clientFavorites.find(
              home => home._id === this.foundProperty.propertyId
            )
          }
        } else {
          const propertyObject = await this.setFavorite(this.foundProperty._id)
          requestedProperty = await this.requestTourForProperty(propertyObject)
        }
        if (requestedProperty && requestedProperty.propertyId) {
          requestedProperty = {
            ...this.foundProperty,
            ...requestedProperty,
            favoriteId: requestedProperty._id,
            _id: this.foundProperty._id
          }
          this.setRht({
            ...this.rhtValue,
            data: [requestedProperty, ...this.rhtValue.data]
          })
          this.requestedTourToAdd.push({
            ...requestedProperty,
            checkbox: true
          })
          this.addRequestedHomes()
        }
        this.confirmModalNegative()
      } else {
        // Handling positive click for property removal modal
        if (this.selectedForRemoval) {
          // If property is selected for removal
          this.removePropertyFromBeingAdded()
        } else {
          // Case of delete route
          this.deleteRoute()
          this.confirmModalNegative()
        }
      }
    },

    confirmModalNegative() {
      this.confirmationModal = JSON.parse(
        JSON.stringify(DEFAULT_CONFIRMATION_MODAL_VALUE)
      )
      this.selectedForRemoval = null
      this.foundProperty = null
    },

    removePropertyFromBeingAdded() {
      this.setRouteEditMode(true)
      this.resetStartEnd = true
      if (this.selectedForRemoval._id) {
        // If selected property is a type of MLS property
        const updatedHomes = this.currentRoute.fullCalculatedArray.filter(
          item => item._id !== this.selectedForRemoval._id
        )
        this.updateRouteObjectPropertyParams(updatedHomes)
      } else {
        // If selected property is custom stop
        let updatedCalculatedArray = [...this.currentRoute.calculatedArray]

        const removedCustomAddress = this.selectedForRemoval.Address
        if (removedCustomAddress.includes('Custom Start Location')) {
          updatedCalculatedArray.shift()
        }
        if (removedCustomAddress.includes('Custom End Location')) {
          updatedCalculatedArray.pop()
        }

        let homeList = []
        if (!isEmpty(this.currentRoute.fullCalculatedArray)) {
          homeList = [...this.currentRoute.fullCalculatedArray]
        }
        this.setUpdatedListAfterRemove(updatedCalculatedArray, homeList)
      }
      this.confirmModalNegative()
    },

    // Function to update full route homes, calculated table data
    // and individual markers for current custom route
    updateRouteObjectPropertyParams(homeList) {
      const current =
        !isEmpty(this.newCustomRoute) &&
        !isEmpty(this.newCustomRoute.calculatedArray)
          ? [...this.newCustomRoute.calculatedArray]
          : []
      let updatedCalculatedArray = []
      if (hasCustomStartStop(current)) {
        updatedCalculatedArray.push(current[0])
      }
      homeList.forEach(home => {
        updatedCalculatedArray.push({
          Address: propertyFullAddress(home),
          arrivalTime: null,
          latitude: home.Latitude,
          longitude: home.Longitude,
          propertyId: home._id
        })
      })

      if (hasCustomEndStop(current)) {
        updatedCalculatedArray.push(current[current.length - 1])
      }

      this.setUpdatedListAfterRemove(updatedCalculatedArray, homeList)
    },

    setUpdatedListAfterRemove(updatedCalculatedArray, homeList) {
      if (hasCalculatedArrayTime(updatedCalculatedArray)) {
        updatedCalculatedArray = this.resetStopTimes(updatedCalculatedArray)
      }

      if (!isEmpty(updatedCalculatedArray)) {
        this.newCustomRoute = {
          ...this.currentRoute,
          fullCalculatedArray: [...homeList],
          calculatedArray: [...updatedCalculatedArray],
          markers: this.setMarkers(updatedCalculatedArray),
          returnedRoute: null
        }
        this.setStartEndLocationList(updatedCalculatedArray)
      } else {
        this.clearHomeList()
      }
    },

    // Calculate the route for entered properties
    async calculate() {
      this.isCalculating = true
      const jsDate = new Date(
        `${this.chosenDate.replace(/-/gi, '-')} ${this.chosenTime}`
      ) // only works for future dates
      const startTime = moment(jsDate)
        .utc()
        .format()

      let propertyStops = [...this.currentRoute.calculatedArray]
      let homeData = [...this.currentRoute.fullCalculatedArray]
      let filteredWaypoints = [...homeData]

      const sourceProperty = propertyStops[0]
      let source = sourceProperty.latitude
        ? `${sourceProperty.latitude}, ${sourceProperty.longitude}`
        : sourceProperty.Address
      let destination = null

      if (propertyStops.length > 1) {
        const destinationProperty = propertyStops[propertyStops.length - 1]

        destination = destinationProperty.latitude
          ? `${destinationProperty.latitude}, ${destinationProperty.longitude}`
          : destinationProperty.Address
      }

      let stops = []
      if (filteredWaypoints.length > 0) {
        for (const house of filteredWaypoints) {
          const latLng = house.Latitude + ', ' + house.Longitude
          stops.push({
            location: latLng,
            stopover: true
          })
        }
        if (!this.isCustomStart) stops.shift()
        if (!this.isCustomEnd) stops.pop()
      }

      let calculatedArray = []
      let returnedRoute = null
      if (propertyStops.length > 1) {
        returnedRoute = await setRoute(source, destination, stops)

        if (returnedRoute.status === 'OK') {
          const route = returnedRoute.routes[0]

          if (this.isCustomStart) {
            const sourceLeg = route.legs[0]
            const startLocationObject = {
              Address: `Custom Start Location: ${sourceLeg.start_address}`,
              arrivalTime: startTime,
              latitude: sourceLeg.start_location.lat(),
              longitude: sourceLeg.start_location.lng(),
              propertyId: null
            }
            calculatedArray.push(startLocationObject)
          } else {
            const calculatedProperty = {
              Address: propertyFullAddress(homeData[0]),
              arrivalTime: startTime,
              latitude: homeData[0].Latitude,
              longitude: homeData[0].Longitude,
              propertyId: homeData[0]._id
            }
            calculatedArray.push(calculatedProperty)
          }

          let timeIterator = [startTime]

          let waypointLegIndices = []
          for (const i of route.waypoint_order) {
            let counter = i + 1
            if (this.isCustomStart) counter = i
            const lastTime = timeIterator[timeIterator.length - 1]

            const thisTime = moment(lastTime)
              .add(route.legs[i].duration.value + 60 * this.viewTime, 'seconds')
              .utc()
              .format()

            timeIterator.push(roundOffMinutes(15, moment(thisTime)))

            waypointLegIndices.push(i)
            const calculatedProperty = {
              Address: propertyFullAddress(homeData[counter]),
              arrivalTime: roundOffMinutes(15, moment(thisTime)),
              latitude: homeData[counter].Latitude,
              longitude: homeData[counter].Longitude,
              propertyId: homeData[counter]._id
            }
            calculatedArray.push(calculatedProperty)
          }

          const lastTime = timeIterator[timeIterator.length - 1]
          let destinationLegIndex = 0
          if (waypointLegIndices.length > 0) {
            destinationLegIndex = waypointLegIndices.length
          }
          const thisTime = moment(lastTime)
            .add(
              route.legs[destinationLegIndex].duration.value +
                60 * this.viewTime,
              'seconds'
            )
            .utc()
            .format()

          if (this.isCustomEnd) {
            let destinationLeg = route.legs.find(
              (leg, index) => !waypointLegIndices.includes(index)
            )
            const endLocationObject = {
              Address: `Custom End Location: ${destinationLeg.end_address}`,
              arrivalTime: roundOffMinutes(15, moment(thisTime)),
              latitude: destinationLeg.end_location.lat(),
              longitude: destinationLeg.end_location.lng(),
              propertyId: null
            }
            calculatedArray.push(endLocationObject)
          } else {
            let destinationLeg = homeData[homeData.length - 1]
            const calculatedProperty = {
              Address: propertyFullAddress(destinationLeg),
              arrivalTime: roundOffMinutes(15, moment(thisTime)),
              latitude: destinationLeg.Latitude,
              longitude: destinationLeg.Longitude,
              propertyId: destinationLeg._id
            }
            calculatedArray.push(calculatedProperty)
          }
          this.calculateTableItems(calculatedArray)
          this.isCalculating = false
        } else {
          this.handleGoogleDirectionsError(returnedRoute.status)
          this.isCalculating = false
        }
      } else {
        calculatedArray = [
          {
            Address: propertyFullAddress(homeData[0]),
            arrivalTime: startTime,
            latitude: homeData[0].Latitude,
            longitude: homeData[0].Longitude,
            propertyId: homeData[0]._id
          }
        ]
        this.calculateTableItems(calculatedArray)
        this.isCalculating = false
      }

      this.newCustomRoute = {
        ...this.newCustomRoute,
        calculatedArray,
        markers: this.setMarkers(calculatedArray),
        returnedRoute
      }
    },

    handleGoogleDirectionsError(responseStatus) {
      let modalErrorMsg = null
      switch (responseStatus) {
        case 'ZERO_RESULTS':
          modalErrorMsg =
            "Couldn't create a route from provided start/end location. If end location is empty, then try another start location. Else try another nearby end location."
          break
        case 'NOT_FOUND':
          modalErrorMsg =
            "End Location entered couldn't be found. Please check that it exists or try to enter another nearby location"
          break
        default:
          modalErrorMsg = `Error in calculating route - ${toCapitalCase(
            responseStatus
          )}`
      }
      Object.assign(this.warningModalParams, {
        title: modalErrorMsg,
        btnText: 'Ok'
      })
      this.displayWarningModal = true
      this.isCalculating = false
    },

    dateUpdater(dateFromChild) {
      this.chosenDate = dateFromChild
      if (!this.routeEditMode) this.activateRouteEditMode()
      Object.assign(this.calendarObject, {
        start: moment(dateFromChild, 'MM/DD/YYYY').format('YYYY-MM-DD')
      })
    },

    timeUpdater(timeFromChild) {
      if (isTimePresent(this.routeTimeSpan, timeFromChild)) {
        // warning modal here and warning related state changes
        Object.assign(this.warningModalParams, {
          title:
            "That time is either booked or might conflict with another route that's already present. Please consider using another available time and/or date.",
          btnText: 'Ok'
        })
        this.displayWarningModal = true
        this.timeError = true
        this.chosenTime = ''
      } else {
        if (!this.routeEditMode) {
          this.setRouteEditMode(true)
        }
        // const updatedCalculatedArray = this.fieldValuesChanged()
        // this.newCustomRoute = {
        //   ...this.currentRoute,
        //   calculatedArray: [...updatedCalculatedArray]
        // }
        this.timeError = false
        this.chosenTime = timeFromChild
      }
    },

    activateRouteEditMode(updatedArray = null) {
      if (updatedArray || !isEmpty(this.currentRoute)) {
        if (!this.routeEditMode) this.setRouteEditMode(true)
        let updatedCalculatedArray = updatedArray
          ? [...updatedArray]
          : [...this.currentRoute.calculatedArray]

        if (
          hasCalculatedArrayTime(this.currentRoute.calculatedArray) ||
          hasDirectionRoute(this.currentRoute)
        ) {
          updatedCalculatedArray = this.resetStopTimes(updatedCalculatedArray)
        }

        this.newCustomRoute = {
          ...this.currentRoute,
          calculatedArray: updatedCalculatedArray,
          markers: this.setMarkers(updatedCalculatedArray),
          returnedRoute: null
        }
      }
    },

    resetStopTimes(data) {
      if (isCustomStopCalculated(this.selectedStartLocation)) {
        this.setSelectedStartLocation({
          ...this.selectedStartLocation,
          arrivalTime: null
        })
      }
      if (isCustomStopCalculated(this.selectedEndLocation)) {
        this.setSelectedEndLocation({
          ...this.selectedEndLocation,
          arrivalTime: null
        })
      }
      return resetCalculatedTimes(data)
    },

    closeWarningModal() {
      this.displayWarningModal = false
    },

    saveBtnClicked() {
      const dataArray = [...this.currentRoute.calculatedArray]
      const routeEndTime = dataArray[dataArray.length - 1].arrivalTime
      if (
        isTimePresent(this.routeTimeSpan, moment(routeEndTime).format('HH:mm'))
      ) {
        Object.assign(this.warningModalParams, {
          title:
            "The end time for this route is conflicting with another route that's already present. Please consider using another available time and/or date.",
          btnText: 'Ok'
        })
        this.displayWarningModal = true
      } else if (moment(routeEndTime).format('HH:mm') > '21:00') {
        Object.assign(this.warningModalParams, {
          title:
            'The end time for this route is exceeding the maximum time limit of 9:00PM for showing a route.',
          btnText: 'Ok'
        })
        this.displayWarningModal = true
      } else {
        if (this.isSavedRoute) {
          this.updateRoute()
        } else {
          this.openNameModal = !this.openNameModal
        }
      }
    },

    // Function to save a new route
    async saveRoute() {
      const postRequest = this.setPostRequestParams()
      this.startProcess('Saving your new route')

      const response = await saveRouteRequest(
        this.clientData._id,
        postRequest,
        { params: { optimize: true } }
      )
      if (response.status === 201) {
        let updatedRoute = {
          ...response.data,
          clientId: this.clientData._id,
          clientName: this.clientData.name
        }
        this.setAgentRouteData(
          sortObjectArrayByKey([updatedRoute, ...this.agentRouteData]),
          'startTime',
          'desc'
        )
        this.refreshData(
          updatedRoute,
          `Successfully saved your new route ${postRequest.name}`
        )
      } else if (response.status === 409) {
        // Show the conflict error message
        this.openWarningModal(
          `Route with name ${postRequest.name} already exists. Please save using another route name`
        )
        this.waitForProgress = false
        this.spinnerText = null
        this.setSelectedRouteLoading(false)
        this.routeName = ''
      } else {
        // Show other error messages
        this.handlingErrorFunction(
          `Some error occurred while saving this route. Please try again later.`
        )
      }
    },

    async updateRoute() {
      const postRequest = this.setPostRequestParams()
      this.startProcess('Updating your route')
      // Adding params to request object in alignment with PUT request
      const putRequest = {
        ...postRequest,
        name: this.selectedRoute.name,
        _id: this.selectedRoute._id,
        state: this.selectedRoute.state
      }

      const response = await updateRouteRequest(
        this.clientData._id,
        this.selectedRoute._id,
        putRequest
      )
      if (response.status === 200) {
        const updatedRoute = this.routesUpdateAfterActions(
          response.data,
          this.clientData,
          this.agentRouteData
        )
        this.refreshData(
          updatedRoute,
          `Successfully updated your route ${putRequest.name}`
        )
      } else {
        this.handlingErrorFunction(
          `Some error occurred while updating this route`
        )
      }
    },

    setVariablesToDefault(spinner = true) {
      this.setSelectedRouteLoading(false)
      this.routeName = ''
      this.setRouteEditMode(false)
      if (spinner) {
        this.waitForProgress = false
        this.spinnerText = ''
      }
      this.timeFromRoute = ''
    },

    // Function to set params before save/update route
    setPostRequestParams() {
      const dataArray = [...this.currentRoute.calculatedArray]
      const startTime = dataArray[0].arrivalTime
      const startLocation = [dataArray[0].latitude, dataArray[0].longitude]

      let customStartLocation = false
      let customEndLocation = false

      if (hasCustomStartStop(dataArray)) {
        customStartLocation = true
        dataArray.shift()
      }

      let routeStops = []
      for (const calc of dataArray) {
        routeStops.push({
          propertyId: calc.propertyId,
          arrivalTime: calc.arrivalTime
        })
      }

      if (hasCustomEndStop(dataArray)) {
        customEndLocation = true
        routeStops.pop()
      }

      const lastCalc = dataArray[dataArray.length - 1]
      const endLocation = [lastCalc.latitude, lastCalc.longitude]
      return {
        name: this.routeName,
        startLocation,
        endLocation,
        startTime,
        routeStops,
        customStartLocation,
        customEndLocation,
        viewingTimeMins: this.viewTime
      }
    },

    nameReceived(name) {
      if (name) {
        this.routeName = name
        this.openNameModal = false
        if (this.isSelectedRoute) {
          this.updateRoute()
        } else {
          this.saveRoute()
        }
      }
    },

    getMinTime() {
      let momentObject = moment(this.chosenDate, 'MM/DD/YYYY')
      if (momentObject.isSame(moment(), 'day')) {
        const minTime = moment()
          .add(60, 'minutes')
          .format('HH:mm')
        const midnightTime = moment('00:00', 'HH:mm')
        const minMorningTime = moment('6:00', 'HH:mm')
        const minTimeDate = moment(minTime, 'HH:mm').format('MM/DD/YYYY')
        if (minTimeDate === this.chosenDate) {
          if (moment(minTime, 'HH:mm').isAfter(moment('6:00', 'HH:mm'))) {
            return minTime
          }
        } else if (
          moment(minTime, 'HH:mm').isBetween(midnightTime, minMorningTime)
        ) {
          return '00:00'
        }
      }
      return '6:00'
    },

    startProcess(msg) {
      this.setSelectedRouteLoading(true)
      this.waitForProgress = true
      this.spinnerText = msg
    },

    handlingErrorFunction(message) {
      this.waitForProgress = false
      this.spinnerText = null
      this.setSelectedRouteLoading(false)
      this.routeName = ''
      this.alertObject = Object.assign(
        {},
        {
          type: 'error',
          value: true,
          message
        }
      )
    },

    newRoutePage() {
      this.setRouteEditMode(true)
      this.clearCustomStartEnd()
      this.setSelectedRoute({})
      this.clearHomeList()
      this.clearTimer = true
      this.chosenTime = ''
      this.setRouteTimeSpan(this.agentRouteData, this.chosenDate)
      this.setVariablesToDefault(false)
    },

    clearHomeList() {
      this.newCustomRoute = {}
      this.updatedTableItems = []
      this.requestedTourToAdd = []
      const newTourHomes = this.filteredRhtValue.map(home => home)
      for (const favorite of newTourHomes) {
        this.requestedTourToAdd.push({ ...favorite, checkbox: false })
      }
      this.setRouteHomes([])
    },

    async requestShowing() {
      this.startProcess(
        'Sending requests to listing agents for confirmation of time slot'
      )

      const response = await requestRouteShowing(this.selectedRoute)

      if (response.status === 200) {
        const message = updateSavedRoutesOnPendingRequest(
          this.clientData,
          response.data
        )
        const updatedRoute = this.routesUpdateAfterActions(
          response.data,
          this.clientData,
          this.agentRouteData
        )
        this.refreshData(updatedRoute, message)
        this.updateRhtAfterRequestShowing(response.data)
      } else if (response.status === 422) {
        this.handlingErrorFunction('Unprocessable entity')
      } else {
        this.handlingErrorFunction('Some error occurred. Please try again')
      }
    },

    updateRouteStates(routeData, params) {
      return params.calculatedArray.map(item => {
        if (item.propertyId) {
          const foundStop = routeData.routeStops.find(
            stop => stop.propertyId === item.propertyId
          )
          item = { ...item, ...foundStop }
        }
        return item
      })
    },

    async sendSchedule(isReminder = false) {
      this.startProcess('Sending Confirmed schedule to the client')

      const response = await sendScheduleRequest(this.selectedRoute)
      if (response.status === 200) {
        let updatedRoute = { ...response.data }

        updatedRoute = this.routesUpdateAfterActions(
          updatedRoute,
          this.clientData,
          this.agentRouteData
        )

        const subject = isReminder ? 'Schedule Reminder' : `Scheduled Route`

        await sendRouteSchedule({
          to: [this.clientData._id],
          message: 'Route Details',
          date: moment(response.data.startTime).format('LLLL'),
          subject,
          routeId: response.data._id
        })
        this.refreshData(
          updatedRoute,
          `Schedule has been sent to the client with route details`
        )
      }
    },

    cancelRoute() {
      Object.assign(this.confirmationModal, {
        status: true,
        message: `Do you really want to cancel this route? This process cannot be undone.`,
        positive: 'Delete route',
        negative: 'Cancel'
      })
    },

    // Function to delete a route
    async deleteRoute() {
      this.startProcess(`Route ${this.selectedRoute.name} is being removed`)
      const responseObject = await cancelDeleteRoute(this.selectedRoute)
      if (!isEmpty(responseObject)) {
        const { isSaved, data } = responseObject
        if (!isSaved) {
          this.updateRhtAfterCancelRoute(data)
          this.updateAgentRoutesOnCancel(data)
        } else {
          this.setAgentRouteData(
            this.agentRouteData.filter(route => route._id !== data._id)
          )
        }
        this.updateClientDataOnCancel(this.clientData, data, isSaved)
        this.refreshData(
          data,
          `Successfully cancelled route ${data.name}`
        )
        this.newRoutePage()
        this.waitForProgress = false
        this.spinnerText = null
      } else {
        this.handlingErrorFunction(
          'Some error occurred while deleting the route. Please try again'
        )
      }
    },

    refreshData(data, message = null) {
      if (data) this.setSelectedRoute(data)
      this.setVariablesToDefault()
      if (message) {
        this.alertObject = Object.assign(
          {},
          {
            type: 'success',
            value: true,
            message
          }
        )
      }
    }
  }
}
</script>

<style lang="sass">
.disable-element
  @extend %disabled-state

.block-heading
  position: relative
  margin-bottom: 8%
  &::after
    position: absolute
    content: ''
    left: 5%
    right: 5%
    bottom: -10px
    height: 1px
    background-color: $hwayzce

.block-filled
  border: 1px solid $hwayzYellow !important
</style>

<style lang="sass" scoped>
.plan-route-container
  text-align: left
  height: 100%
  background-color: $hwayzWhite

.wrapper
  padding: 1% 2% 2% 2%
  width: 100%
  display: grid
  grid-template-areas: "firstrow middlerow lastrow"
  grid-template-columns: 2fr 3fr 2fr
  grid-gap: 2%

.calendar_wrap
  grid-area: firstrow
  display: grid
  grid-template-areas: "calendar"
  grid-template-rows: 1fr

.calendar
  border: 1px solid $hwayzce
  grid-area: calendar
  padding: 5%

.typename
  grid-area: routename

.middle-blocks
  grid-area: middlerow
  display: grid
  height: 80vh
  grid-template-areas: "time" "location" "viewing" "calculation"
  grid-template-columns: 1fr
  grid-template-rows: 1fr 1fr 1fr 4fr
  grid-row-gap: 3%
  &.single
    grid-template-areas: "calculation"
    grid-template-rows: 1fr
    height: 75vh

.choose_time, .calculate, .type_name
  border: 1px solid $hwayzce
  text-align: center

.calculate, .type_name
  padding: 5%

.choose_time
  padding: calc(10% / 3) 5% 0
  grid-area: time

.calculate
  padding: 5% 0 2% 0
  grid-area: calculation
  margin-bottom: 2rem
  &.small-padding
    padding: calc(10% / 3) 0 2% 0

.two-entries
  display: grid
  grid-template-areas: "date time"
  grid-template-columns: 1fr 1fr
  grid-column-gap: 5%
  align-items: center
  margin-top: 10px

.date
  grid-area: date

.time
  grid-area: time

.rightside
  grid-area: lastrow
  display: grid
  grid-template-areas: "addHome" "map"
  grid-template-columns: 1fr
  grid-template-rows: 2fr 3fr
  grid-row-gap: 5%
  height: 85vh

.map
  grid-area: map

#client-name
  font-size: 14px
  width: 60%
  color: $hwayzBlack
  align-self: center !important

.disable-element
  @extend %disabled-state
</style>
